export const MESSAGE_NOTIFICATION_NAVIGATE_EVENT =
  "sparelynk:message-notification-navigate";

export type MessageNotificationNavigationDetail = {
  href: string;
};

export type MessageNotificationCandidate = {
  key: string;
  threadKey?: string;
  senderRole: string;
  senderName: string;
  message: string;
  createdAt?: string;
  readAt?: string;
  href?: string;
  imageUrl?: string;
  title?: string;
};

type NotificationState = {
  initialized: boolean;
  seen: Record<string, number>;
  readAtByThread: Record<string, number>;
};

export type ProductContextMessage = {
  context?: {
    type?: string;
    id?: string;
    productId?: string;
    label?: string;
    products?: Array<{ name?: string }>;
  };
};

export function getMessageProductContextId(
  message: ProductContextMessage,
): string {
  const contextType = String(message.context?.type ?? "")
    .trim()
    .toLowerCase();
  return String(
    message.context?.productId ||
      (contextType === "product" || contextType === "products"
        ? message.context?.id
        : "") ||
      "",
  ).trim();
}

export function messageMatchesProductContext(
  message: ProductContextMessage,
  productId: string,
): boolean {
  return getMessageProductContextId(message) === productId.trim();
}

export function getMessageProductContextLabel(
  message: ProductContextMessage,
): string {
  const productName = message.context?.products?.[0]?.name?.trim();
  if (productName) return productName;

  const label = String(message.context?.label ?? "")
    .replace(/^Assessment question\s*[·|-]\s*/i, "")
    .trim();
  return label || "this product";
}

export function getQualityAssessorRequestNotificationTitle(
  message: ProductContextMessage,
): string {
  return `Quality Assessor requested more proof for ${getMessageProductContextLabel(message)}`;
}

export function buildSellerShopNotificationHref(
  buyerId: string,
  productId?: string,
): string {
  const params = new URLSearchParams();
  params.set("messages", "open");
  params.set("buyerId", buyerId.trim());
  if (productId?.trim()) {
    params.set("productId", productId.trim());
  }
  return `/seller/dashboard?${params.toString()}`;
}

export function buildQualityAssessorShopNotificationHref(
  vendorId: string,
  productId?: string,
): string {
  const params = new URLSearchParams();
  params.set("messages", "open");
  params.set("vendorId", vendorId.trim());
  if (productId?.trim()) {
    params.set("productId", productId.trim());
  }
  return `/quality-assessor/products?${params.toString()}`;
}

export function getSellerResponseNotificationTitle(
  message: ProductContextMessage,
): string {
  return `Seller responded to your proof request for ${getMessageProductContextLabel(message)}`;
}

type NotifyIncomingMessagesInput = {
  userId: string;
  currentRole: string;
  scope: string;
  candidates: MessageNotificationCandidate[];
};

type MarkMessageNotificationThreadReadInput = {
  userId: string;
  scope: string;
  threadKey: string;
  readAt?: string;
};

const STORAGE_PREFIX = "sparelynk-message-notifications";
const MAX_SEEN_MESSAGE_KEYS = 500;
const MAX_READ_THREAD_KEYS = 250;
const MAX_PUSH_NOTIFICATIONS_PER_REFRESH = 3;
const MESSAGE_PANEL_SEARCH_PARAMS = new Set([
  "messages",
  "channel",
  "orderId",
  "order_id",
  "conversationId",
  "conversation_id",
  "buyerId",
  "buyer_id",
  "vendorId",
  "vendor_id",
  "sellerId",
  "seller_id",
  "buyerName",
  "buyer_name",
  "sellerName",
  "seller_name",
  "productId",
  "product_id",
  "contextType",
  "context_type",
  "contextId",
  "context_id",
  "contextLabel",
  "context_label",
  "contextImage",
  "context_image",
]);
const activeBrowserNotifications = new Map<string, Notification>();

function normalizeRole(value: string): string {
  return value.trim().toLowerCase();
}

function getStorageKey(userId: string, scope: string): string {
  return `${STORAGE_PREFIX}:${userId}:${scope}`;
}

function getCandidateThreadKey(
  candidate: MessageNotificationCandidate,
): string {
  return candidate.threadKey?.trim() || candidate.key.trim();
}

function parseTimestamp(value?: string): number {
  const timestamp = value ? Date.parse(value) : Number.NaN;
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function readNotificationState(storageKey: string): NotificationState {
  if (typeof window === "undefined") {
    return { initialized: false, seen: {}, readAtByThread: {} };
  }

  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) {
      return { initialized: false, seen: {}, readAtByThread: {} };
    }

    const parsed = JSON.parse(stored) as Partial<NotificationState>;
    return {
      initialized: parsed.initialized === true,
      seen:
        parsed.seen && typeof parsed.seen === "object"
          ? (parsed.seen as Record<string, number>)
          : {},
      readAtByThread:
        parsed.readAtByThread && typeof parsed.readAtByThread === "object"
          ? (parsed.readAtByThread as Record<string, number>)
          : {},
    };
  } catch {
    return { initialized: false, seen: {}, readAtByThread: {} };
  }
}

function writeNotificationState(
  storageKey: string,
  state: NotificationState,
): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const seenEntries = Object.entries(state.seen)
      .sort((left, right) => right[1] - left[1])
      .slice(0, MAX_SEEN_MESSAGE_KEYS);
    const readEntries = Object.entries(state.readAtByThread)
      .sort((left, right) => right[1] - left[1])
      .slice(0, MAX_READ_THREAD_KEYS);

    window.localStorage.setItem(
      storageKey,
      JSON.stringify({
        initialized: state.initialized,
        seen: Object.fromEntries(seenEntries),
        readAtByThread: Object.fromEntries(readEntries),
      } satisfies NotificationState),
    );
  } catch {
    // Notifications remain functional even when browser storage is unavailable.
  }
}

function getCandidateTimestamp(
  candidate: MessageNotificationCandidate,
): number {
  const timestamp = candidate.createdAt
    ? Date.parse(candidate.createdAt)
    : Number.NaN;

  return Number.isFinite(timestamp) ? timestamp : Date.now();
}

function resolveInternalNotificationHref(href?: string): string | null {
  if (typeof window === "undefined" || !href?.trim()) {
    return null;
  }

  try {
    const target = new URL(href, window.location.href);
    if (target.origin !== window.location.origin) {
      return null;
    }

    const normalizedPath = target.pathname.replace(/\/+$/, "") || "/";
    const isBuyerMessageTarget =
      normalizedPath === "/buyer/conversations" ||
      (normalizedPath === "/buyer/home" &&
        target.searchParams.get("messages") === "open");
    const isSellerMessageTarget =
      normalizedPath === "/seller/conversations" ||
      (normalizedPath === "/seller/dashboard" &&
        target.searchParams.get("messages") === "open");

    if (isBuyerMessageTarget || isSellerMessageTarget) {
      const rolePrefix = isBuyerMessageTarget ? "/buyer" : "/seller";
      const fallbackPath = isBuyerMessageTarget
        ? "/buyer/home"
        : "/seller/dashboard";
      const current = new URL(window.location.href);
      const currentPath = current.pathname.replace(/\/+$/, "") || "/";
      const isCurrentRolePage =
        currentPath === rolePrefix || currentPath.startsWith(`${rolePrefix}/`);
      const isConversationAlias = currentPath === `${rolePrefix}/conversations`;
      const preservedParams = new URLSearchParams(
        isCurrentRolePage && !isConversationAlias ? current.search : "",
      );

      for (const key of MESSAGE_PANEL_SEARCH_PARAMS) {
        preservedParams.delete(key);
      }
      target.searchParams.forEach((value, key) => {
        preservedParams.set(key, value);
      });

      target.pathname =
        isCurrentRolePage && !isConversationAlias
          ? current.pathname
          : fallbackPath;
      target.search = preservedParams.toString();
      target.searchParams.set("messages", "open");
    }

    return `${target.pathname}${target.search}${target.hash}`;
  } catch {
    return null;
  }
}

function openNotificationTarget(href?: string): void {
  if (typeof window === "undefined") {
    return;
  }

  window.focus();
  const targetHref = resolveInternalNotificationHref(href);
  if (!targetHref) {
    return;
  }

  const navigationEvent = new CustomEvent<MessageNotificationNavigationDetail>(
    MESSAGE_NOTIFICATION_NAVIGATE_EVENT,
    {
      detail: { href: targetHref },
      cancelable: true,
    },
  );

  if (!window.dispatchEvent(navigationEvent)) {
    return;
  }

  const currentHref = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (currentHref !== targetHref) {
    window.history.pushState(window.history.state, "", targetHref);
  }
  window.dispatchEvent(
    new PopStateEvent("popstate", { state: window.history.state }),
  );
}

function showBrowserNotification(
  candidate: MessageNotificationCandidate,
  storageKey: string,
): void {
  if (
    typeof Notification === "undefined" ||
    Notification.permission !== "granted"
  ) {
    return;
  }

  const threadKey = getCandidateThreadKey(candidate);
  const activeKey = `${storageKey}:${threadKey}`;
  activeBrowserNotifications.get(activeKey)?.close();

  const notification = new Notification(
    candidate.title ||
      `New message from ${candidate.senderName || "SpareLynk user"}`,
    {
      body: candidate.message,
      tag: `sparelynk-message-thread-${threadKey}`,
      icon: candidate.imageUrl || undefined,
      data: { href: candidate.href, threadKey },
    },
  );
  activeBrowserNotifications.set(activeKey, notification);

  notification.onclick = () => {
    openNotificationTarget(candidate.href);
    notification.close();
  };
  notification.onclose = () => {
    if (activeBrowserNotifications.get(activeKey) === notification) {
      activeBrowserNotifications.delete(activeKey);
    }
  };
}

function dispatchInAppNotification(
  candidate: MessageNotificationCandidate,
): void {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent("sparelynk:new-message", {
      detail: candidate,
    }),
  );
}

export function markMessageNotificationThreadRead({
  userId,
  scope,
  threadKey,
  readAt,
}: MarkMessageNotificationThreadReadInput): void {
  if (
    typeof window === "undefined" ||
    !userId.trim() ||
    !scope.trim() ||
    !threadKey.trim()
  ) {
    return;
  }

  const storageKey = getStorageKey(userId.trim(), scope.trim());
  const state = readNotificationState(storageKey);
  const timestamp = parseTimestamp(readAt) || Date.now();
  const normalizedThreadKey = threadKey.trim();

  state.initialized = true;
  state.readAtByThread[normalizedThreadKey] = Math.max(
    state.readAtByThread[normalizedThreadKey] ?? 0,
    timestamp,
  );
  writeNotificationState(storageKey, state);

  const activeKey = `${storageKey}:${normalizedThreadKey}`;
  activeBrowserNotifications.get(activeKey)?.close();
  activeBrowserNotifications.delete(activeKey);
}

export function notifyNewIncomingMessages({
  userId,
  currentRole,
  scope,
  candidates,
}: NotifyIncomingMessagesInput): MessageNotificationCandidate[] {
  if (typeof window === "undefined" || !userId.trim() || !scope.trim()) {
    return [];
  }

  const role = normalizeRole(currentRole);
  const sortedIncomingCandidates = candidates
    .filter((candidate) => candidate.key.trim())
    .filter((candidate) => normalizeRole(candidate.senderRole) !== role)
    .sort(
      (left, right) =>
        getCandidateTimestamp(left) - getCandidateTimestamp(right),
    );
  const candidatesByKey = new Map<string, MessageNotificationCandidate>();
  sortedIncomingCandidates.forEach((candidate) => {
    candidatesByKey.set(candidate.key, candidate);
  });
  const incomingCandidates = Array.from(candidatesByKey.values());
  const storageKey = getStorageKey(userId.trim(), scope.trim());
  const state = readNotificationState(storageKey);

  incomingCandidates.forEach((candidate) => {
    const threadKey = getCandidateThreadKey(candidate);
    const explicitReadAt = parseTimestamp(candidate.readAt);
    if (explicitReadAt > 0) {
      state.readAtByThread[threadKey] = Math.max(
        state.readAtByThread[threadKey] ?? 0,
        explicitReadAt,
      );
    }
  });

  if (!state.initialized) {
    incomingCandidates.forEach((candidate) => {
      state.seen[candidate.key] = getCandidateTimestamp(candidate);
    });
    state.initialized = true;
    writeNotificationState(storageKey, state);
    return [];
  }

  const newCandidates = incomingCandidates.filter((candidate) => {
    if (state.seen[candidate.key]) return false;
    const threadReadAt =
      state.readAtByThread[getCandidateThreadKey(candidate)] ?? 0;
    return getCandidateTimestamp(candidate) > threadReadAt;
  });

  incomingCandidates.forEach((candidate) => {
    state.seen[candidate.key] = getCandidateTimestamp(candidate);
  });
  writeNotificationState(storageKey, state);

  newCandidates.forEach((candidate, index) => {
    dispatchInAppNotification(candidate);
    if (index < MAX_PUSH_NOTIFICATIONS_PER_REFRESH) {
      showBrowserNotification(candidate, storageKey);
    }
  });

  return newCandidates;
}

export function armMessageNotificationPermission(): () => void {
  if (
    typeof window === "undefined" ||
    typeof Notification === "undefined" ||
    Notification.permission !== "default"
  ) {
    return () => undefined;
  }

  let requested = false;

  const requestPermission = () => {
    if (requested || Notification.permission !== "default") {
      return;
    }

    requested = true;
    void Notification.requestPermission().catch(() => undefined);
    window.removeEventListener("pointerdown", requestPermission);
    window.removeEventListener("keydown", requestPermission);
  };

  window.addEventListener("pointerdown", requestPermission, { once: true });
  window.addEventListener("keydown", requestPermission, { once: true });

  return () => {
    window.removeEventListener("pointerdown", requestPermission);
    window.removeEventListener("keydown", requestPermission);
  };
}
